import React, { useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { useNavigate } from 'react-router-dom';
import { getSavedCardsByGames, getAllSavedCardsWithDetails, getSavedCardsByGame } from '../api/savedCards';
import GamePlayOverlay from '../components/GamePlayOverlay/GamePlayOverlay';
import { getCoverUrl } from '../api/games';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator/PullToRefreshIndicator';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { usePageCache } from '../hooks/usePageCache';
import './SavedPage.css';

export default function SavedPage() {
    // Состояние для выбранной игры (для открытия GamePlayOverlay)
    const [selectedGame, setSelectedGame] = useState(null);
    const [showGameOverlay, setShowGameOverlay] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const navigate = useNavigate();

    const [savedGamesList, setSavedGamesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const loadSavedData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            // Загружаем только игры для списка. "Все карты" загрузим при клике.
            const gamesData = await getSavedCardsByGames();
            setSavedGamesList(gamesData);
            return { games: gamesData };
        } catch (e) {
            console.error('Error loading saved data:', e);
            setError(e.message || String(e));
            throw e;
        } finally {
            setLoading(false);
        }
    }, []);

    const cachedData = usePageCache().getCachedData('saved', loadSavedData, []);

    useEffect(() => {
        if (cachedData.data && isInitialLoad) {
            setSavedGamesList(cachedData.data.games || []);
            setLoading(false);
            setIsInitialLoad(false);
        }
    }, [cachedData.data, isInitialLoad]);

    useEffect(() => {
        if (cachedData.loading !== undefined) {
            setLoading(cachedData.loading);
        }
    }, [cachedData.loading]);

    const handleRefresh = useCallback(async () => {
        await cachedData.refresh();
    }, [cachedData]);

    const {
        elementRef,
        isRefreshing,
        isPulling,
        pullDistance,
        refreshProgress,
        canRefresh
    } = usePullToRefresh(handleRefresh, {
        threshold: 80,
        resistance: 2.5,
        maxPullDistance: 120,
        enabled: !loading && !error
    });

    // Обработчик открытия всех сохраненных карточек
    const handleOpenAllSaved = async () => {
        try {
            const allCards = await getAllSavedCardsWithDetails();
            if (allCards.length > 0) {
                setSelectedGame({
                    id: 'all-saved',
                    title: 'Все сохраненные',
                    savedCards: allCards
                });
                setShowGameOverlay(true);
            }
        } catch (error) {
            console.error('Error opening all saved cards:', error);
        }
    };

    // Обработчик клика по игре
    const handleGameClick = async (game) => {
        try {
            const cards = await getSavedCardsByGame(game.id);
            if (cards.length > 0) {
                setSelectedGame({
                    ...game,
                    savedCards: cards
                });
                setShowGameOverlay(true);
            }
        } catch (error) {
            console.error('Error opening game saved cards:', error);
        }
    };

    const handleCloseOverlay = () => {
        setShowGameOverlay(false);
        setSelectedGame(null);
        cachedData.refresh();
    };

    // Подсчет общего количества
    const totalSavedCards = savedGamesList ? savedGamesList.reduce((acc, game) => acc + (game.saved_count || 0), 0) : 0;

    // Показывать скелетон только при первой загрузке или если нет данных при загрузке
    const showSkeleton = loading && savedGamesList.length === 0;

    if (error) {
        return (
            <main className="page saved-page">
                <h1>Сохраненное</h1>
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                    <h3>Ошибка загрузки данных</h3>
                    <p>{error}</p>
                    <button onClick={() => loadSavedData()} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
                        Попробовать снова
                    </button>
                </div>
            </main>
        );
    }

    return (
        <>
            <PullToRefreshIndicator
                isRefreshing={isRefreshing}
                isPulling={isPulling}
                progress={refreshProgress}
                canRefresh={canRefresh}
                pullDistance={pullDistance}
            />
            <main className="page saved-page" ref={elementRef}>
                <h1>Сохраненное</h1>

                <div className="saved-mode-switch">
                    <button
                        className="mode-btn mode-btn--active"
                        onClick={() => { }}
                    >
                        По играм
                    </button>
                    <button
                        className="mode-btn"
                        onClick={savedGamesList.length > 0 ? handleOpenAllSaved : undefined}
                        style={{ opacity: savedGamesList.length > 0 ? 1 : 0.5, cursor: savedGamesList.length > 0 ? 'pointer' : 'default' }}
                    >
                        Все
                    </button>
                </div>

                {showSkeleton ? (
                    <div className="saved-content">
                        {Array.from({ length: 4 }, (_, i) => (
                            <div key={i}>
                                <div className="skeleton-card" style={{ height: '100px' }} />
                                {i !== 3 && <div className="divider" />}
                            </div>
                        ))}
                    </div>
                ) : savedGamesList.length === 0 ? (
                    <div className="saved-empty-state">
                        <div className="saved-empty-icon">
                            <img src="/icons/bookmark-filled.svg" alt="" />
                        </div>
                        <h3>У вас пока нет сохраненных карточек</h3>
                        <p style={{ color: 'var(--text-secondary)' }}>Сохраняйте понравившиеся карточки во время игры, чтобы вернуться к ним позже</p>
                    </div>
                ) : (
                    <div className="saved-content">
                        <div className="saved-games-list">
                            {savedGamesList.map((game, idx) => (
                                <div key={game.id}>
                                    <div
                                        className="game-card"
                                        onClick={() => handleGameClick(game)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <img
                                            src={getCoverUrl(game)}
                                            alt={game.title}
                                            className="game-cover"
                                        />
                                        <div className="game-meta">
                                            <div className="game-title">{game.title}</div>
                                            <div className="game-subtitle">{game.subtitle}</div>
                                            <div className="saved-game-count">
                                                <img src="/icons/get-demo.svg" alt="" />
                                                <span>{game.saved_count}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {idx !== savedGamesList.length - 1 && <div className="divider" />}
                                </div>
                            ))}
                        </div>

                        {savedGamesList.length > 5 && (
                            <button className="view-all-saved-btn" onClick={handleOpenAllSaved}>
                                Все сохраненные карточки ({totalSavedCards})
                            </button>
                        )}
                    </div>
                )}
            </main>

            {showGameOverlay && selectedGame && (
                <GamePlayOverlay
                    game={selectedGame}
                    onClose={handleCloseOverlay}
                    savedCardsMode={true}
                    initialShowAll={true}
                />
            )}
        </>
    );
}
